import type {
  ShipmentDetailsPageRow,
  ShipmentsDetailsPagePayload,
} from '../api/shipments';

type BoxGroup = [
  id: number,
  quantity: number,
  box_weight: number,
  variety: string | null,
  labeled_variety: string | null,
  caliber: string | null,
  color: string | null,
  category: string | null,
  box_format: string | null,
  box_label: string | null,
  box_type: string | null,
  boxing_rows: number,
];
type BoxField =
  | 'id'
  | 'quantity'
  | 'box_weight'
  | 'variety'
  | 'labeled_variety'
  | 'caliber'
  | 'color'
  | 'category'
  | 'box_format'
  | 'box_label'
  | 'box_type'
  | 'boxing_rows'
  | 'tracking_journeys';

export interface ShipmentOverviewPayload {
  version: 1;
  shipments: (Omit<ShipmentDetailsPageRow, BoxField> & { boxes: BoxGroup[] })[];
  tracking_by_shipment: NonNullable<
    ShipmentsDetailsPagePayload['tracking_by_shipment']
  >;
  status_counts: ShipmentsDetailsPagePayload['status_counts'];
}

export function decodeShipmentOverview(
  payload: ShipmentOverviewPayload
): ShipmentsDetailsPagePayload {
  if (payload?.version !== 1 || !Array.isArray(payload.shipments)) {
    throw new Error('Invalid shipment overview response');
  }
  const trackingByShipment = new Map(
    (payload.tracking_by_shipment || []).map((tracking) => [
      Number(tracking.shipment_id),
      tracking.tracking_journeys,
    ])
  );
  const rows = payload.shipments.flatMap(({ boxes, ...header }) => {
    const tracking_journeys =
      trackingByShipment.get(Number(header.shipment_id)) || [];
    return boxes.map(
      ([
        id,
        quantity,
        box_weight,
        variety,
        labeled_variety,
        caliber,
        color,
        category,
        box_format,
        box_label,
        box_type,
        boxing_rows,
      ]) =>
        ({
          ...header,
          id: String(id),
          quantity,
          box_weight,
          variety,
          labeled_variety,
          caliber,
          color,
          category,
          box_format,
          box_label,
          box_type,
          boxing_rows,
          tracking_journeys,
        }) as ShipmentDetailsPageRow
    );
  });
  // Preserve the original first-contributor semantics for references with
  // different shipping dates and shipment sources.
  rows.sort((left, right) => {
    if (left.ship_etd !== right.ship_etd) {
      if (left.ship_etd == null) return 1;
      if (right.ship_etd == null) return -1;
      return left.ship_etd < right.ship_etd ? -1 : 1;
    }
    const reference = String(left.reference_number).localeCompare(
      String(right.reference_number)
    );
    return reference || Number(left.id) - Number(right.id);
  });
  return { rows, status_counts: payload.status_counts };
}

export const countShipmentBoxRows = (
  rows: { id?: string; boxing_rows?: number }[]
) => rows.reduce((count, row) => count + (row.boxing_rows ?? 1), 0);
